import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { fragmentContainsNodeType } from '@atlaskit/editor-common/insert';
import {
	getParentOfTypeCount,
	getPositionAfterTopParentNodeOfType,
	isNestedTablesSupported,
} from '@atlaskit/editor-common/nesting';
import type { Node as ProsemirrorNode, ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import { Slice, type Fragment, type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { hasParentNodeOfType, safeInsert } from '@atlaskit/editor-prosemirror/utils';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import {
	findFirstReplaceStep,
	getValidEndTextPosition,
} from '../../../../pm-plugins/decoration/utils/step-utils';
/**
 * Figure out what to do with these, possibly export/shift into editor-common
 * during move into editor-core
 */
import { getSupportedNodesForAIModal } from '../../../utils/ai-button';
import { addAnalytics } from '../../../utils/analytics/utils';
import type { Positions } from '../../../utils/xstate/get-ai-experience-service';

/**
 * Unsure if we need this - add this 'hook' for now, started with just meta
 * but also folded in `setSelection` logic to reduce that complexity
 *
 * DO NOT pass transaction around beyond here
 */
export function beforeDispatchAITransaction(tr: Transaction, editorState: EditorState) {
	/**
	 * DO NOT REMOVE isAiContentTransformation from transaction meta.
	 * Proactive AI S+G rely on isAiContentTransformation meta to
	 * 	ignore changes in document made by AI modal.
	 * Why ignore it?
	 * - Response returned from AI does not require Spelling and Grammar check.
	 * - We have assumed that it's already correct from S+G perspective.
	 */
	tr.setMeta('isAiContentTransformation', true);

	const firstReplaceStep = findFirstReplaceStep(tr.steps);
	if (!firstReplaceStep) {
		// Ideally we should always have replace step for for AI transaction.
		// But in case if there isn't we want to fire analytic event so we can monitor it.
		addAnalytics({
			editorState,
			tr,
			payload: {
				action: 'failed',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'experienceApplication',
				attributes: {
					errorType: 'replaceStepNotFound',
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			},
		});
		return tr;
	}

	const validPositionToSet = getValidEndTextPosition({
		step: firstReplaceStep,
		doc: tr.doc,
	});

	if (validPositionToSet !== 0) {
		tr.setSelection(TextSelection.create(tr.doc, validPositionToSet));
	}

	return tr;
}

export function beforeDispatchingTransactionForBlocks(
	fragment: Fragment,
	tr: Transaction,
	state: EditorState,
) {
	const supportedNodeTypes = getSupportedNodesForAIModal(state);
	// Here we are checking if there is text node at first level.
	// Then we will highlight all nodes.
	let areTopLevelBlockElements = true;
	fragment.forEach((node) => {
		areTopLevelBlockElements = areTopLevelBlockElements && supportedNodeTypes.has(node.type);
	});

	if (areTopLevelBlockElements) {
		return tr.setMeta('isAiContentTransformationForBlocks', true);
	}
	return tr;
}

export function beforeDispatchAIRovoAgentTransaction(tr: Transaction) {
	/**
	 * DO NOT REMOVE
	 * This is used when distinguishing content generated by a Rovo agent
	 */
	tr.setMeta('isAiRovoAgentContentTransformation', true);

	return tr;
}
function getLastContentDepth({ pmFragment }: { pmFragment: Fragment }) {
	let depth = 0;
	let currentNode: Fragment | ProsemirrorNode = pmFragment;

	while (currentNode.firstChild) {
		depth++;
		currentNode = currentNode.firstChild;
	}

	return depth;
}

function isSelectionAtParagraphStart($pos: ResolvedPos): boolean {
	const node = $pos.parent;
	return (
		!!node && (node.type.name === 'paragraph' || node.type.name === 'heading') && !$pos.parentOffset
	);
}

function isFragmentSingleParagraph(fragment: Fragment): boolean {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return fragment.childCount === 1 && fragment.firstChild!.type.name === 'paragraph';
}

function isSelectionInTaskOrDecisionNode($pos: ResolvedPos): boolean {
	const node = $pos.parent;
	return (
		!!node &&
		(node.type.name === 'decisionList' ||
			node.type.name === 'decisionItem' ||
			node.type.name === 'taskList' ||
			node.type.name === 'taskItem')
	);
}

/**
 * If nested tables experiment is on and the selection is inside a table,
 * we check if the selection is too nested to insert a new table to determine
 * if we should insert after the top table node.
 *
 * We want to limit table nesting to only allowing 1 table to be nested inside another table.
 */
export function shouldInsertAfterTable({
	selection,
	tableSchema,
}: {
	selection: Selection;
	tableSchema: NodeType;
}): boolean {
	const isNestedTablesDisabled = editorExperiment('nested-tables-in-tables', false, {
		exposure: true,
	});

	const parentTableCount = getParentOfTypeCount(tableSchema)(selection.$from);

	const hasParentTable = hasParentNodeOfType(tableSchema)(selection);

	return hasParentTable && (isNestedTablesDisabled || parentTableCount > 1);
}

/**
 * Insert after the top table node if the selection is too nested.
 */
export const insertContentAfterTable = ({
	pmFragment,
	transaction,
}: {
	pmFragment: Fragment;
	transaction: Transaction;
}): Transaction => {
	const tableSchema = transaction.doc.type.schema.nodes.table;
	const fromPos = transaction.selection.$from;
	const positionAfterTopTable = getPositionAfterTopParentNodeOfType(tableSchema)(fromPos);

	return safeInsert(pmFragment, positionAfterTopTable)(transaction);
};

export const replaceWithAIContentForBlock = ({
	editorView,
	positions,
	pmFragment,
}: {
	editorView: EditorView;
	pmFragment: Fragment;
		positions: Positions;
}): Transaction => {
	const { state } = editorView;
	let transaction = state.tr;

	const slice =
		pmFragment.size === 0 && fg('platform_editor_ai_replace_content_empty_slice')
			? Slice.empty
			: new Slice(pmFragment, 0, 0);

	if (pmFragment.size === 0 && fg('platform_editor_ai_replace_content_empty_slice')) {
		transaction = addAnalytics({
			editorState: state,
			tr: transaction,
			payload: {
				action: 'fallback',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'replaceWithEmptyAIContent',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					isBlock: true,
					isSelectionEmpty: positions[0] === positions[1],
				},
			},
		});
	}

	try {
		// If you can't replace the range, use safeInsert
		transaction = transaction.replaceRange(positions[0], positions[1], slice);
	} catch (error) {
		// If you can't replace the range, use safeInsert
		transaction = safeInsert(
			pmFragment,
			undefined,
			true,
		)(
			addAnalytics({
				editorState: state,
				tr: transaction,
				payload: {
					action: 'fallback',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'proseMirrorReplace',
					eventType: EVENT_TYPE.OPERATIONAL,
					attributes: {
						target: 'replaceWithAIContentForBlock',
						isSelectionEmpty: positions[0] === positions[1],
						errorMessage: ErrorUtils.extractErrorMessage(error),
						isReplacementEmpty: pmFragment.size === 0,
					},
				},
			}),
		);
	}
	return beforeDispatchAITransaction(
		beforeDispatchingTransactionForBlocks(pmFragment, transaction, state),
		state,
	);
};

export const replaceWithAIContent = ({
	editorView,
	positions,
	pmFragment,
	selectionMetaData,
	options = {
		replaceDocOnEmptySelection: true,
	},
}: {
	editorView: EditorView;
	options?: {
		replaceDocOnEmptySelection?: boolean;
	};
	pmFragment: Fragment;
	positions: Positions;
	selectionMetaData?: {
		sliceOpenEnd?: number;
		sliceOpenStart?: number;
	};
}): Transaction => {
	const { state } = editorView;
	let transaction = state.tr;
	const endPositionDepth = state.doc.resolve(positions[1]).depth;
	const lastFragmentDepth = getLastContentDepth({ pmFragment });
	// selectionMetaData will always be undefined when adf streaming is disabled
	const sliceOpenStart = selectionMetaData?.sliceOpenStart || 1;
	const sliceOpenEnd =
		selectionMetaData?.sliceOpenEnd || Math.min(endPositionDepth, lastFragmentDepth);
	const slice =
		pmFragment.size === 0 && fg('platform_editor_ai_replace_content_empty_slice')
			? Slice.empty
			: new Slice(pmFragment, sliceOpenStart, sliceOpenEnd);

	if (pmFragment.size === 0 && fg('platform_editor_ai_replace_content_empty_slice')) {
		transaction = addAnalytics({
			editorState: state,
			tr: transaction,
			payload: {
				action: 'fallback',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'replaceWithEmptyAIContent',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					isBlock: false,
					isSelectionEmpty: positions[0] === positions[1],
				},
			},
		});
	}

	if (
		fragmentContainsNodeType(pmFragment, transaction.doc.type.schema.nodes.table) &&
		shouldInsertAfterTable({
			selection: transaction.selection,
			tableSchema: transaction.doc.type.schema.nodes.table,
		}) &&
		isNestedTablesSupported(state.schema)
	) {
		return insertContentAfterTable({
			pmFragment,
			transaction,
		});
	} else {
		// We check if the positions are the same if so we replace the whole document
		// This is a quick spike so real implementation might need to be different
		// You might need to create a new replace action
		if (options.replaceDocOnEmptySelection && positions[0] === positions[1]) {
			transaction = transaction.replaceRange(0, state.doc.content.size, slice);
		} else {
			try {
				transaction = transaction.replaceRange(positions[0], positions[1], slice);
				// If you can't replace the range, use safeInsert
			} catch (error) {
				transaction = safeInsert(
					pmFragment,
					undefined,
					true,
				)(
					addAnalytics({
						editorState: state,
						tr: transaction,
						payload: {
							action: 'fallback',
							actionSubject: 'editorPluginAI',
							actionSubjectId: 'proseMirrorReplace',
							eventType: EVENT_TYPE.OPERATIONAL,
							attributes: {
								target: 'replaceWithAIContent',
								isSelectionEmpty: positions[0] === positions[1],
								errorMessage: ErrorUtils.extractErrorMessage(error),
								isReplacementEmpty: pmFragment.size === 0,
							},
						},
					}),
				);
			}
		}
		return beforeDispatchAITransaction(transaction, state);
	}
};

export const insertAIContentAtCurrentPosition = ({
	editorView,
	positions,
	pmFragment,
}: {
	editorView: EditorView;
	pmFragment: Fragment;
	positions: Positions;
}): Transaction => {
	const { state } = editorView;
	const transaction = state.tr;
	const pos = positions[0];
	const $pos = state.doc.resolve(pos);
	const isParagraphStart = isSelectionAtParagraphStart($pos);
	// if selection is at the beginning of paragraph,
	// insert before the cursor so that it inserts without an extra new line
	const insertPos = isParagraphStart ? pos - 1 : pos;

	// if selection is inside decision or task node AND pmFragment is a single paragraph
	// strip the paragraph so it can insert inside the decision/task
	let fragmentToInsert = pmFragment;
	if (isSelectionInTaskOrDecisionNode($pos) && isFragmentSingleParagraph(pmFragment)) {
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		fragmentToInsert = pmFragment.firstChild!.content;
	}

	if (
		fragmentContainsNodeType(pmFragment, transaction.doc.type.schema.nodes.table) &&
		shouldInsertAfterTable({
			selection: transaction.selection,
			tableSchema: transaction.doc.type.schema.nodes.table,
		}) &&
		isNestedTablesSupported(state.schema)
	) {
		return insertContentAfterTable({
			pmFragment,
			transaction,
		});
	} else {
		return beforeDispatchAITransaction(transaction.insert(insertPos, fragmentToInsert), state);
	}
};
